import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PasswordInput } from "@/components/PasswordInput";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Plus, UserPlus2, Trash2, Eye, EyeOff } from "lucide-react";

const ROLE_LABEL = {
  admin: "Admin",
  pmc_supervisor: "Park Supervisor (ITCOT)",
  contractor_supervisor: "Contractor Supervisor",
  supervisor_incharge: "Supervisor In-Charge",
  contractor_admin: "Contractor Office Admin",
};

const EMPTY_FORM = {
  role: "pmc_supervisor",
  username: "",
  full_name: "",
  email: "",
  phone: "",
  password: "",
  confirm: "",
  park_ids: [],
  contractor_id: "",
};

/** User-management card for the Settings page. Lists every user (admin /
 *  PMC / contractor / in-charge / office admin) and lets the admin:
 *    - create new users (any role, with park / contractor assignments)
 *    - edit username, password, park assignments
 *    - delete users (admins cannot delete themselves)
 */
export function CredentialsManager() {
  const [users, setUsers] = useState([]);
  const [parks, setParks] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [showPasswords, setShowPasswords] = useState({});

  // Single dialog used for both create + edit modes.
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create"); // 'create' | 'edit'
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, p, c] = await Promise.all([
        api.get("/users"),
        api.get("/parks"),
        api.get("/contractors"),
      ]);
      setUsers(u.data);
      setParks(p.data);
      setContractors(c.data);
    } catch {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const parksById = useMemo(() => Object.fromEntries(parks.map((p) => [p.id, p])), [parks]);
  const contractorsById = useMemo(() => Object.fromEntries(contractors.map((c) => [c.id, c])), [contractors]);

  const openCreate = () => {
    setMode("create");
    setTarget(null);
    setForm({ ...EMPTY_FORM });
    setOpen(true);
  };

  const openEdit = (u) => {
    const currentPass = u.plain_password || u.username || "";
    setMode("edit");
    setTarget(u);
    setForm({
      role: u.role,
      username: u.username,
      full_name: u.full_name,
      email: u.email || "",
      phone: u.phone || "",
      password: currentPass,
      confirm: currentPass,
      park_ids: u.park_ids || [],
      contractor_id: u.contractor_id || "",
    });
    setOpen(true);
  };

  const togglePark = (pid) => {
    if (form.role === "pmc_supervisor" || form.role === "supervisor_incharge") {
      const has = form.park_ids.includes(pid);
      if (has) setForm({ ...form, park_ids: form.park_ids.filter((x) => x !== pid) });
      else if (form.role === "pmc_supervisor" && form.park_ids.length >= 4) toast.error("PMC supervisor cannot have more than 4 parks");
      else setForm({ ...form, park_ids: [...form.park_ids, pid] });
    } else if (form.role === "contractor_supervisor") {
      // contractor supervisor = exactly one park
      setForm({ ...form, park_ids: [pid] });
    }
  };

  const submit = async () => {
    if (!form.username.trim() || !form.full_name.trim()) return toast.error("Username and full name required");

    if (mode === "create") {
      if (!form.password) return toast.error("Password required");
      if (form.password !== form.confirm) return toast.error("Passwords do not match");
    } else if (form.password && form.password !== form.confirm) {
      return toast.error("Passwords do not match");
    }

    if (form.role === "pmc_supervisor" && (form.park_ids.length < 1 || form.park_ids.length > 4)) {
      return toast.error("Assign between 1 and 4 parks");
    }
    if (form.role === "contractor_supervisor") {
      if (!form.contractor_id) return toast.error("Pick a contractor");
      if (form.park_ids.length !== 1) return toast.error("Assign exactly 1 park");
    }
    if (form.role === "supervisor_incharge") {
      if (!form.contractor_id) return toast.error("Pick a contractor");
    }
    if (form.role === "contractor_admin") {
      if (!form.contractor_id) return toast.error("Pick a contractor");
    }

    setSaving(true);
    try {
      if (mode === "create") {
        const payload = {
          role: form.role,
          username: form.username.trim(),
          full_name: form.full_name.trim(),
          email: form.email || null,
          phone: form.phone || null,
          password: form.password,
          park_ids: ["admin", "contractor_admin"].includes(form.role) ? [] : form.park_ids,
          contractor_id: ["contractor_supervisor", "supervisor_incharge", "contractor_admin"].includes(form.role) ? form.contractor_id : null,
        };
        await api.post("/users", payload);
        toast.success("User created");
      } else {
        const payload = {};
        if (form.username !== target.username) payload.username = form.username.trim();
        if (form.password) payload.password = form.password;
        if (form.full_name !== target.full_name) payload.full_name = form.full_name.trim();
        if ((form.email || "") !== (target.email || "")) payload.email = form.email || null;
        if ((form.phone || "") !== (target.phone || "")) payload.phone = form.phone || null;
        if (target.role !== "admin") payload.park_ids = form.park_ids;
        if (["contractor_supervisor", "supervisor_incharge", "contractor_admin"].includes(target.role)) payload.contractor_id = form.contractor_id;
        if (Object.keys(payload).length === 0) {
          toast.message("No changes");
          setOpen(false);
          return;
        }
        await api.put(`/users/${target.id}`, payload);
        toast.success("Credentials updated");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user '${u.full_name}' (@${u.username})? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = users.filter((u) => {
    if (filter === "all") return true;
    if (filter === "supervisor_incharge") {
      return u.role === "supervisor_incharge" || u.username === "supervisor_incharge";
    }
    if (filter === "contractor_supervisor") {
      return u.role === "contractor_supervisor" && u.username !== "supervisor_incharge";
    }
    return u.role === filter;
  });

  const FILTERS = [
    { id: "all", label: "All" },
    { id: "admin", label: "Admin" },
    { id: "pmc_supervisor", label: "Park Supervisors" },
    { id: "contractor_supervisor", label: "Contractor Supervisors" },
    { id: "supervisor_incharge", label: "Supervisor In-Charge" },
    { id: "contractor_admin", label: "Contractor Office Admin" },
  ];

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="h-4 w-4" /> User credentials</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1" data-testid="creds-filter">
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`rounded-full border px-2.5 py-1 text-[11px] ${filter === f.id ? "bg-primary text-primary-foreground border-primary" : "hover:bg-secondary"}`}
                  data-testid={`creds-filter-${f.id}`}
                >{f.label}</button>
              ))}
            </div>
            <Button size="sm" onClick={openCreate} data-testid="creds-add-user-button">
              <UserPlus2 className="mr-1.5 h-3.5 w-3.5" />Add user
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-sm text-muted-foreground">Loading…</div>
            : filtered.length === 0 ? <div className="text-sm text-muted-foreground py-4">No users for this filter.</div>
            : <div className="overflow-x-auto rounded-md border" data-testid="creds-user-list">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 text-[11px] uppercase tracking-wider text-slate-700">
                  <tr className="border-b border-slate-200">
                    <th className="whitespace-nowrap px-3 py-2 text-center">#</th>
                    <th className="whitespace-nowrap px-3 py-2 text-left">Name</th>
                    <th className="whitespace-nowrap px-3 py-2 text-left">Username</th>
                    <th className="whitespace-nowrap px-3 py-2 text-left">Password</th>
                    <th className="whitespace-nowrap px-3 py-2 text-center">Role</th>
                    <th className="whitespace-nowrap px-3 py-2 text-left">Contractor</th>
                    <th className="whitespace-nowrap px-3 py-2 text-left">Assigned parks</th>
                    <th className="whitespace-nowrap px-3 py-2 text-center">Status</th>
                    <th className="whitespace-nowrap px-3 py-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u, idx) => (
                    <tr key={u.id} className={`border-b border-slate-100 ${u.active === false ? "bg-slate-100 text-slate-500" : "hover:bg-amber-50/50"}`} data-testid={`creds-row-${u.username}`}>
                      <td className="whitespace-nowrap px-3 py-2 text-center font-mono text-xs text-slate-500 tabular-nums">{idx + 1}</td>
                      <td className="whitespace-nowrap px-3 py-2 text-left">
                        <span className="font-medium text-slate-900">{u.full_name}</span>
                        {u.role === "pmc_supervisor" && u.id_card_number && (
                          <span className="ml-2 text-[10px] text-slate-500">ID {u.id_card_number}</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-left">
                        <span className="font-mono text-xs text-slate-700">@{u.username}</span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-left">
                        <div className="flex items-center gap-1.5" data-testid={`creds-row-pass-${u.username}`}>
                          <span className="font-mono text-xs text-slate-700">
                            {showPasswords[u.id] ? (u.plain_password || u.username || "••••••••") : "••••••••"}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowPasswords((prev) => ({ ...prev, [u.id]: !prev[u.id] }))}
                            className="text-slate-400 hover:text-slate-600 focus:outline-none"
                            title={showPasswords[u.id] ? "Hide password" : "Show password"}
                          >
                            {showPasswords[u.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                          </button>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-center">
                        <span className="rounded-full bg-amber-200 text-amber-900 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                          {ROLE_LABEL[u.role] || (u.username === "supervisor_incharge" ? "Supervisor In-Charge" : u.role)}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-left text-xs text-slate-700">
                        {["contractor_supervisor", "supervisor_incharge", "contractor_admin"].includes(u.role)
                          ? (contractorsById[u.contractor_id]?.name || "—")
                          : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-left">
                        {u.role === "admin" ? <span className="text-slate-400 text-xs">All access</span>
                          : (u.park_ids || []).length === 0
                            ? <span className="italic text-xs text-slate-400">no parks assigned</span>
                            : (
                              <div className="flex flex-wrap gap-1">
                                {(u.park_ids || []).map((pid) => (
                                  <span key={pid} className="rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[10px] font-mono font-semibold" data-testid={`creds-row-park-${u.username}-${pid}`}>
                                    {parksById[pid]?.code || pid.slice(0, 6)}
                                  </span>
                                ))}
                                {u.park_ids?.length === 4 && (
                                  <span className="rounded bg-amber-200 text-amber-900 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">rotation</span>
                                )}
                              </div>
                            )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-center">
                        {u.active === false
                          ? <span className="inline-flex items-center gap-1 rounded-full bg-slate-300 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">Inactive</span>
                          : <span className="inline-flex items-center gap-1 rounded-full bg-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-900">Active</span>}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-center">
                        <div className="flex justify-center gap-1">
                          <Button size="sm" variant="outline" className="h-7" onClick={() => openEdit(u)} data-testid={`creds-edit-${u.username}`}>
                            <KeyRound className="mr-1.5 h-3.5 w-3.5" />Edit
                          </Button>
                          {u.role !== "admin" && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" onClick={() => remove(u)} data-testid={`creds-delete-${u.username}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {mode === "create" ? <><Plus className="h-4 w-4" />New user</> : <><KeyRound className="h-4 w-4" />Edit user</>}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-3 py-2">
            {/* Role picker (create only) */}
            {mode === "create" && (
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v)=>setForm({...form, role: v, park_ids: [], contractor_id: ""})}>
                  <SelectTrigger data-testid="creds-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="pmc_supervisor">Park Supervisor (ITCOT)</SelectItem>
                    <SelectItem value="contractor_supervisor">Contractor Supervisor</SelectItem>
                    <SelectItem value="supervisor_incharge">Supervisor In-Charge</SelectItem>
                    <SelectItem value="contractor_admin">Contractor Office Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">Username *</Label>
                <Input value={form.username} onChange={(e)=>setForm({...form, username: e.target.value})} data-testid="creds-form-username" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">Full name *</Label>
                <Input value={form.full_name} onChange={(e)=>setForm({...form, full_name: e.target.value})} data-testid="creds-form-fullname" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">Phone</Label>
                <Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">Email</Label>
                <Input value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">{mode === "edit" ? "Password *" : "Password *"}</Label>
                <PasswordInput value={form.password} onChange={(e)=>setForm({...form, password: e.target.value})} data-testid="creds-form-password" />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">Confirm password</Label>
                <PasswordInput value={form.confirm} onChange={(e)=>setForm({...form, confirm: e.target.value})} data-testid="creds-form-confirm" />
              </div>
            </div>

            {/* Contractor picker (contractor_supervisor / supervisor_incharge / contractor_admin) */}
            {["contractor_supervisor", "supervisor_incharge", "contractor_admin"].includes(form.role) && (
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">Contractor agency *</Label>
                <Select value={form.contractor_id} onValueChange={(v)=>setForm({...form, contractor_id: v})}>
                  <SelectTrigger data-testid="creds-form-contractor"><SelectValue placeholder="Pick contractor" /></SelectTrigger>
                  <SelectContent>
                    {contractors.length === 0
                      ? <div className="px-3 py-4 text-xs text-muted-foreground">No contractors yet</div>
                      : contractors.map((c)=><SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)
                    }
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Park assignments (PMC + contractor + supervisor_incharge) */}
            {["pmc_supervisor", "contractor_supervisor", "supervisor_incharge"].includes(form.role) && (
              <div className="flex flex-col gap-1.5">
                <Label className="text-xs">
                  {form.role === "pmc_supervisor"
                    ? `Assigned parks (1–4) · ${form.park_ids.length} selected`
                    : form.role === "supervisor_incharge"
                    ? `Assigned parks · ${form.park_ids.length} selected`
                    : `Assigned park (exactly 1) · ${form.park_ids.length} selected`}
                </Label>
                {parks.length === 0 ? (
                  <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">No parks yet — add some in Settings → Data Management → Parks.</div>
                ) : (
                  <div className="flex max-h-44 flex-wrap gap-1.5 overflow-y-auto rounded-md border bg-secondary/30 p-2" data-testid="creds-form-parks">
                    {parks.map((p) => {
                      const selected = form.park_ids.includes(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() => togglePark(p.id)}
                          className={`rounded-full border px-2.5 py-1 text-[11px] transition-colors ${selected ? "border-amber-400 bg-amber-100 text-amber-900 font-medium" : "border-slate-200 bg-white hover:bg-slate-50 text-slate-700"}`}
                          data-testid={`creds-form-park-${p.code}`}
                        >
                          {p.code} — {p.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="creds-save-button">{saving ? "Saving…" : (mode === "create" ? "Create" : "Save changes")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
